import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

export const LinkDialog = ({ open, stationId, isAdmin, onClose, onChanged }) => {
  const [s, setS] = useState(null);
  const [km, setKm] = useState("");
  const toggleDc = async (v, key = "double_crew") => {
    try { const { data } = await api.put("/assignments/settings", { default_km: Number(s.default_km), [key]: v }, { params: { station_id: stationId } }); setS(data); toast.success(v ? "Opzione attivata" : "Opzione disattivata"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => {
    if (!open) return;
    setS(null);
    setKm("");
    api.get("/assignments/settings", { params: { station_id: stationId } }).then((r) => { setS(r.data); setKm(String(r.data.default_km)); });
  }, [open, stationId]);
  const link = s ? `${window.location.origin}/giornale/${s.token}` : "";
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copiato"); } catch { toast.error("Copia non riuscita: seleziona il testo"); } };
  const saveKm = async () => {
    try { const { data } = await api.put("/assignments/settings", { default_km: Number(km) }, { params: { station_id: stationId } }); setS(data); toast.success("Km di default salvati"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const regen = async () => {
    if (!window.confirm("Creare un nuovo link? Quello vecchio smetterà di funzionare.")) return;
    try { const { data } = await api.post("/assignments/settings/new-link", null, { params: { station_id: stationId } }); setS(data); toast.success("Nuovo link creato"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="link-dialog">
        <DialogHeader>
          <DialogTitle>Giornale di bordo dei driver</DialogTitle>
          <DialogDescription>Link fisso della stazione, senza registrazione: il driver sceglie il suo nome e il furgone tra le assegnazioni di oggi e inserisce i km.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Link da condividere con i driver</Label>
          <div className="flex gap-2">
            <Input readOnly value={link} onFocus={(e) => e.target.select()} data-testid="logbook-link-input" />
            <Button onClick={copy} data-testid="logbook-link-copy"><Copy className="h-4 w-4" /></Button>
          </div>
          {isAdmin && <Button size="sm" variant="ghost" onClick={regen} data-testid="logbook-link-regen"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Crea nuovo link</Button>}
        </div>
        <div className="space-y-1.5 border-t pt-4">
          <Label>Km di default se il driver non compila entro mezzanotte</Label>
          {isAdmin ? (
            <div className="flex gap-2">
              <Input type="number" min="0" value={km} onChange={(e) => setKm(e.target.value)} className="w-36" data-testid="default-km-input" />
              <Button onClick={saveKm} disabled={km === ""} data-testid="default-km-save">Salva</Button>
            </div>
          ) : <p className="font-mono-num text-lg font-semibold" data-testid="default-km-value">{s?.default_km ?? "—"} km</p>}
          <p className="text-xs text-slate-500">Si applica alle assegnazioni ancora da compilare quando passa la mezzanotte.</p>
        </div>
        <div className="space-y-1.5 border-t pt-4">
          <Label>Doppio equipaggio (2 autisti sullo stesso furgone, con cambio guida)</Label>
          {isAdmin && s ? (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.double_crew} onChange={(e) => toggleDc(e.target.checked)} data-testid="double-crew-toggle" /> Attivo per questa stazione</label>
          ) : <p className="text-sm font-semibold" data-testid="double-crew-value">{s?.double_crew ? "Attivo" : "Non attivo"}</p>}
        </div>
        <div className="space-y-1.5 border-t pt-4">
          <Label>Gasolio inserito dai driver nel giornale di bordo (poi verificato dall'operatore)</Label>
          {isAdmin && s ? (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.driver_fuel} onChange={(e) => toggleDc(e.target.checked, "driver_fuel")} data-testid="driver-fuel-toggle" /> Attivo per questa stazione</label>
          ) : <p className="text-sm font-semibold" data-testid="driver-fuel-value">{s?.driver_fuel ? "Attivo" : "Non attivo"}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
